import { Component, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { SampleTrackingStorage } from '../../sample-tracking-storage';
import { SampleTrackingDetailsHeader, SampleEQueryData } from '../interfaces/sample-search';
import { GridDetails, GridData, GridEvent } from '../interfaces/grid';
import { VisitDetails } from '../interfaces/visit-data';
import { SponsorStudy } from '../../../sponsor-dashboard/sponsor-study';
import { concatMap, map } from 'rxjs/operators';
import { Observable, forkJoin, of } from 'rxjs';
import { UrlBuilder } from '../../../core';
import { TrackingOverviewService } from '../service/tracking-overview.service';
import { ActivityLogger } from '../../../activities/activity-logger';
import { SampleDetails, SampleStatus } from '../interfaces/sample-details';
import { SampleActivity, ActivityStatus } from '../interfaces/sample-activity';
import { BreadcrumbService, IBreadcrumb } from '../../shared/breadcrumb-service/breadcrumb.service';
import { ActivatedRoute } from '@angular/router';
import * as moment from 'moment';

@Component({
    selector: 'app-sample-overview',
    template: require('./sample-overview.component.html'),
    styles: [require('./sample-overview.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SampleOverviewComponent {

    public sampleSearchData: SampleTrackingDetailsHeader;
    public gridDetails: GridDetails;
    public visitDetails: VisitDetails;
    public sampleDetails: SampleDetails;
    public eQueryDetails: SampleEQueryData;
    public activityDetails: SampleActivity[];
    public isLoadingCompleted = false;
    public isSamePageNavigation = true;
    public isSampleOnHold = false;
    public equeryOpenDate: string = moment().subtract(1, 'days').format('YYYY-MM-DD');

    constructor(
        private sampleTrackingStorage: SampleTrackingStorage,
        private changeDectRef: ChangeDetectorRef,
        private urlBuilder: UrlBuilder,
        private trackingService: TrackingOverviewService,
        private activityLogger: ActivityLogger,
        private breadcrumbService: BreadcrumbService,
        private activatedRoute: ActivatedRoute,
    ) {
        if (!this.sampleTrackingStorage.sampleSearchData) {
            this.urlBuilder.esa().sampletracking().home().navigate();
            return;
        }

        this.activatedRoute.params.subscribe((id) => {
            this.changeDectRef.markForCheck();
            this.isLoadingCompleted = false;
            this.getSampleOverviewDetails();
        });

        activityLogger.logActivity('Access Sample Tracking Sample Details');
    }

    public onClickCreateReport() {
        const fileNameToDownload = 'Sample Tracking Details ' + this.sampleSearchData.accessionNumber;
        this.trackingService.createReportForSample(this.gridDetails, this.visitDetails, this.sampleDetails, this.sampleSearchData.currentSampleStatus, fileNameToDownload);
    }

    public onAccessionNumberClick() {

        this.isSamePageNavigation = false;
        this.sampleTrackingStorage.sampleSearchData = new Observable(ob => ob.next(this.sampleSearchData));
        this.urlBuilder
            .esa()
            .sampletracking()
            .visitDetails()
            .navigate();
    }

    public onParentBarcodeClick() {
        this.isSamePageNavigation = true;

        const splitData = this.sampleSearchData.parentContainer.split('-');
        this.sampleSearchData = {
            accessionNumber: splitData[0],
            containerNumber: splitData[1]
        };

        this.sampleTrackingStorage.sampleSearchData = new Observable(ob => ob.next(this.sampleSearchData));

        this.urlBuilder
            .esa()
            .sampletracking()
            .sampleDetails(+this.sampleSearchData.containerNumber)
            .navigate();
    }

    public onGridItemClick(selectedItem: GridEvent) {

        this.isSamePageNavigation = true;
        if (selectedItem.data.barcodeNumber) {
            const splitData = selectedItem.data.barcodeNumber.split('-');
            this.sampleSearchData = {
                accessionNumber: splitData[0],
                containerNumber: splitData[1],
                currentSampleStatus: selectedItem.data.currentSampleStatus as SampleStatus
            };

            this.sampleTrackingStorage.sampleSearchData = new Observable(ob => ob.next(this.sampleSearchData));

            this.urlBuilder
                .esa()
                .sampletracking()
                .sampleDetails(+this.sampleSearchData.containerNumber)
                .navigate();
        }

    }

    public onEQueryButtonClick() {
        let sponsorStudy: SponsorStudy = new SponsorStudy();
        sponsorStudy.protocolId = this.eQueryDetails.protocolFK;
        const toDate = moment().local().format('YYYY-MM-DD');
       
        const eqUrl = this.urlBuilder
            .esa()
            .sponsor()
            .study(sponsorStudy)
            .activityReports()
            .documents('equery')
            .getUrl({ queryParams: { 'date': `${this.equeryOpenDate}To${toDate}`, 'accessionNumber': this.eQueryDetails.accessionNumber } });
        window.open(eqUrl);
    }

    private getSampleOverviewDetails() {
        this.isLoadingCompleted = false;
        this.gridDetails = { header: null, data: null };
        this.visitDetails = { header: null, data: null };
        this.sampleDetails = { header: null, data: null };
        this.activityDetails = [] as SampleActivity[];

        this.sampleTrackingStorage.sampleSearchData
            .pipe(
                map((sampleSearch) => {
                    this.sampleSearchData = sampleSearch;
                    this.sampleSearchData.currentSampleStatus = SampleStatus.NA;
                    return sampleSearch;
                }),
                concatMap((ss) => this.sampleTrackingStorage.getSampleHeaderDetails(ss.accessionNumber, ss.containerNumber)
                ),
                map((headerDetails) => {
                    if (headerDetails && headerDetails.sampleModel) {
                        this.sampleSearchData = headerDetails.sampleModel;
                        this.isSampleOnHold = headerDetails.isSampleOnHold;
                        this.sampleSearchData.isChild = !!this.sampleSearchData.parentBarcode;
                    }

                    if (headerDetails && headerDetails.eQueryOpenHoldModel) {
                        this.eQueryDetails = headerDetails.eQueryOpenHoldModel;
                    }

                    this.sampleSearchData.statusCssClass = this.setStatusCssClass(this.sampleSearchData.currentSampleStatus);
                    return this.sampleSearchData;
                }),
                concatMap((ss) => {
                    if (!!ss.accessionNumber && !!ss.containerNumber && !!ss.barcode && !!ss.currentSampleStatus && !!ss.containerId) {
                        return forkJoin([
                            this.sampleTrackingStorage.getReportData(ss.accessionNumber, ss.barcode, ss.containerNumber),
                            this.sampleTrackingStorage.getVisitData(ss.accessionNumber, ss.containerNumber),
                            this.sampleTrackingStorage.getSampleDetails(ss.accessionNumber, ss.currentSampleStatus.toString().replace(/ /g, ''), ss.containerNumber),
                            this.sampleTrackingStorage.getSampleActivityData(ss.containerId),
                            this.sampleTrackingStorage.getOpenEqueryQuery(ss.accessionNumber),
                        ]);
                    } else {
                        return of([]);
                    }
                }),
                map(([gridData, visitData, sampleData, activityData, equeryData]) => {

                    let routeData: IBreadcrumb = {
                        label: `Sample ${this.sampleSearchData.accessionNumber}-${this.sampleSearchData.containerNumber}`,
                        url: this.isSamePageNavigation ?
                            this.urlBuilder.esa().sampletracking().sampleDetails(+this.sampleSearchData.containerNumber).url() :
                            this.urlBuilder.esa().sampletracking().visitDetails().url(),
                        data: this.sampleSearchData
                    };

                    this.breadcrumbService.AddRouterToBreadCrumb(routeData);

                    this.isLoadingCompleted = true;

                    if (gridData && gridData.sampleAliquot && gridData.sampleAliquot.length) {

                        this.sampleSearchData.isParent = true;

                        gridData.sampleAliquot.forEach((pa: GridData) => {
                            pa.aliquotCreatedDate = pa.parentAliquotDate || pa.childAliquotDate;
                            return pa;
                        });
                    }

                    this.gridDetails = { header: this.trackingService.aliquotsGridHeader, data: gridData.sampleAliquot };

                    if (visitData && visitData.visitDetail) {
                        const data = visitData.visitDetail;
                        data.investigatorNameAndNo = data.investigatorName + ' / ' + data.investigatorNumber;
                        data.visitNameAndPvc = data.visitName + ' / ' + data.protocolVisitCode;

                        if (data.isAgeProject) {
                            data.displayAge = (data.age && data.age.value && data.age.unit) ? data.age.value + ' ' + data.age.unit : '';
                        } else {
                            data.displayAge = (data.birthDate) ? moment(data.birthDate).local().format('DD-MMM-YYYY') : '';
                        }

                        this.visitDetails = { header: this.trackingService.visitDetailsHeading, data };
                    }

                    if (sampleData && sampleData.sampleDetailsData) {
                        const data = sampleData.sampleDetailsData;
                        data.aliquotedDate = data.parentAliquotDate || data.childAliquotDate;


                        const status = this.sampleSearchData.currentSampleStatus;
                        let header = this.trackingService.sampleDetailsHeading;

                        if (status === SampleStatus.Discarded) {
                            header = this.trackingService.sampleDetailsHeadingForDiscarded;
                        }

                        if (status === SampleStatus.Cancelled) {
                            header = this.trackingService.sampleDetailsHeadingForCancelled;
                            // header = header.filter(t => t.key !== 'aliquotedDate');
                        }

                        if (status === SampleStatus.NotReceived) {
                            header = this.trackingService.sampleDetailsHeadingForNotReceived;
                        }

                        if (status === SampleStatus.InInventory || status === SampleStatus.Shipped) {
                            header = this.trackingService.sampleDetailsHeadingForInInventoryOrShipped;
                        }

                        this.sampleDetails = { header, data };
                    }

                    if (activityData && activityData.sampleActivityData && activityData.sampleActivityData.length) {
                        
                        this.activityDetails = activityData.sampleActivityData.filter((sa: SampleActivity) => sa.activityStatus);
                        this.activityDetails.forEach(t => {
                            if (t.activityStatus === ActivityStatus.visitcollected && t.activityOccurredonDate) {
                                t.collectionDate = moment(t.activityOccurredonDate).format('DD-MMM-YYYY');
                                t.collectionTime = moment(t.activityOccurredonDate).format('hh:mm');
                            }
                            if (t.activityOccurredonDate) {
                                t.formattedDateTime = moment(t.activityOccurredonDate).format('DD-MMM-YYYY hh:mm a');
                            }
                        });
                    }

                    if (equeryData && equeryData.equeryOpenData && equeryData.equeryOpenData.equeryOpenDate) {
                        this.equeryOpenDate = moment(equeryData.equeryOpenData.equeryOpenDate).subtract(1, 'days').format('YYYY-MM-DD');
                    }

                    this.changeDectRef.markForCheck();
                })
            )
            .subscribe();
    }

    private setStatusCssClass(currentSampleStatus: SampleStatus) {
        let cssClass = 'success';

        if (currentSampleStatus === SampleStatus.Discarded || currentSampleStatus === SampleStatus.NotReceived) {
            cssClass = 'error';
        }
        if (currentSampleStatus === SampleStatus.Cancelled) {
            cssClass = 'cancel';
        }
        return cssClass;
    }
}
